import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, MessageSquare, Bug, ChevronDown, ChevronUp } from 'lucide-react';

interface RecoveryAction {
  action_id: string;
  label: string;
  handler: string;
  primary?: boolean;
}

interface StandardErrorResponse {
  error_type: string;
  user_message: string;
  recovery_actions: RecoveryAction[];
  technical_details?: string;
  conversation_state_id?: string;
  timestamp: string;
  request_trace_id: string;
  status_code?: number;
}

interface ErrorRecoveryDialogProps {
  error: StandardErrorResponse;
  onRetry?: () => void;
  onSaveState?: () => void;
  onContactSupport?: () => void;
  onClose?: () => void;
}

/**
 * User-friendly error handling with clear recovery options
 * Visual Design:
 * - Non-threatening color scheme (amber, not red)
 * - Clear explanation of what happened
 * - 1-3 recovery action buttons
 * - Option to preserve conversation state
 */
export function ErrorRecoveryDialog({ 
  error: rawError, 
  onRetry, 
  onSaveState,
  onContactSupport,
  onClose
}: ErrorRecoveryDialogProps) {
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  // Normalize error shape — callers may pass non-standard error objects (e.g., WebSocket errors)
  const error: StandardErrorResponse = {
    error_type: rawError?.error_type || (rawError as any)?.code || 'unknown_error',
    user_message: rawError?.user_message || (rawError as any)?.message || 'An unexpected error occurred.',
    recovery_actions: Array.isArray(rawError?.recovery_actions) && rawError.recovery_actions.length > 0 && typeof rawError.recovery_actions[0] === 'object'
      ? rawError.recovery_actions
      : [],
    technical_details: rawError?.technical_details,
    conversation_state_id: rawError?.conversation_state_id,
    timestamp: rawError?.timestamp || new Date().toISOString(),
    request_trace_id: rawError?.request_trace_id || (rawError as any)?.trace_id || `err_${Date.now()}`,
    status_code: rawError?.status_code,
  };

  const handleRecoveryAction = (action: RecoveryAction) => {
    // Map action handlers to callbacks
    switch (action.handler) {
      case 'retryRequest':
        onRetry?.();
        break;
      case 'save_state':
      case 'saveState':
        onSaveState?.();
        break;
      case 'openSupportChat':
      case 'contactSupport':
        onContactSupport?.();
        break;
      case 'reload_page':
      case 'reloadPage':
        window.location.reload();
        break;
      case 'navigateBack':
        window.history.back();
        break;
      default:
        console.warn(`Unknown recovery handler: ${action.handler}`);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="error-dialog-title"
      >
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 id="error-dialog-title" className="text-lg font-semibold text-gray-900">
                Emma encountered an issue
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                {new Date(error.timestamp).toLocaleString()}
              </p>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close dialog"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* User Message */}
          <div className="mb-4">
            <p className="text-gray-800 leading-relaxed">
              {error.user_message}
            </p>
          </div>

          {/* Error Type Badge */}
          <div className="mb-4">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Error type: {error.error_type.replace(/_/g, ' ')}
            </span>
          </div>

          {/* Recovery Actions */}
          {error.recovery_actions.length > 0 && (
            <div className="space-y-2 mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">What would you like to do?</p>
              {error.recovery_actions.map((action) => (
                <button
                  key={action.action_id}
                  onClick={() => handleRecoveryAction(action)}
                  className={`
                    w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md
                    transition-all duration-200 font-medium text-sm
                    ${action.primary 
                      ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-md hover:shadow-lg' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  {getActionIcon(action.action_id)}
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Conversation State ID */}
          {error.conversation_state_id && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs text-blue-800">
                <strong>Your conversation is saved</strong><br />
                We've preserved your progress so you can continue later.
              </p>
            </div>
          )}

          {/* Technical Details (Collapsible) */}
          {error.technical_details && (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <button
                onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-sm font-medium text-gray-700 transition-colors"
              >
                <span>Technical details (for support)</span>
                {showTechnicalDetails ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {showTechnicalDetails && (
                <div className="p-4 bg-gray-900 text-gray-100">
                  <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto">
                    {error.technical_details}
                  </pre>
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <p className="text-xs text-gray-400">
                      <strong>Trace ID:</strong> {error.request_trace_id}
                    </p>
                    {error.status_code && (
                      <p className="text-xs text-gray-400 mt-1">
                        <strong>Status Code:</strong> {error.status_code}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `Error: ${error.error_type}\nTrace ID: ${error.request_trace_id}\n\n${error.technical_details}`
                      );
                    }}
                    className="mt-2 text-xs text-blue-400 hover:text-blue-300 underline"
                  >
                    Copy to clipboard
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            Need help? Quote trace ID <code className="font-mono bg-gray-200 px-1 py-0.5 rounded">{(error.request_trace_id || '').slice(0, 8)}</code> when contacting support.
          </p>
        </div>
      </div>
    </div>
  );
}

function getActionIcon(actionId: string) {
  switch (actionId) {
    case 'retry':
      return <RefreshCw className="w-4 h-4" />;
    case 'contact_support':
    case 'contact_admin':
      return <MessageSquare className="w-4 h-4" />;
    case 'report_bug':
      return <Bug className="w-4 h-4" />;
    default:
      return null;
  }
}
